import { BASKET_TYPES, contractGroupSigned, finalizeBasketManually, isContractGroupSigned, isCustomerDataIncomplete, selectBasket, selectBasketContractGroups, selectBasketCustomerValidation, selectBasketIsPaid, updateTerms } from 'containers/OrderDetails/basketSlice'
import SignAgreementPopup from 'containers/OrderContract/SignAgreementPopup'
import ConsentPreviewPopup from 'components/popups/ConsentPreview'
import useCheckout, { CONTRACT_MODES } from './hooks/useCheckout'
import { useHistory, useLocation } from 'react-router-dom'
import { useReduxDispatch, useReduxSelector } from 'store'
import useBasketFinalize from 'hooks/useBasketFinalize'
import useGroupedBasket from 'hooks/useGroupedBasket'
import useWarehouseName from 'hooks/useWarehouseName'
import useOrderRedirect from 'hooks/useOrderRedirect'
import ArrowBtn from 'components/buttons/ArrowBtn'
import useTranslation from 'hooks/useTranslation'
import Checkbox from 'components/inputs/Checkbox'
import BackBtn from 'components/buttons/BackBtn'
import DeliverySection from './DeliverySection'
import usePopupState from 'hooks/usePopupState'
import SummarySection from './SummarySection'
import { toPhoneString } from 'utils/string'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import AddonSection from './AddonSection'
import UserSection from './UserSection'
import { selectConfig } from 'appSlice'
import Option from 'components/Option'
import MagSection from './MagSection'
import './style.scss'

const OrderSummary = () => {
  const [error, setError] = useState({})
  const config = useReduxSelector(selectConfig)
  const [isFinalized, setFinalized] = useState(false)
  const [isSubmitting, setSubmitting] = useState(false)
  const [isSignedPending, setSignedPending] = useState(false)
  const isPaid = useReduxSelector(selectBasketIsPaid)
  const contractGroups = useReduxSelector(selectBasketContractGroups)
  const customerValidation = useReduxSelector(selectBasketCustomerValidation)
  const { tenant, invoice, terms, contractConfirmation, contractTemplateUrl } = useSelector(selectBasket)
  const { popup, openPopup } = usePopupState()
  const { mode, isLoading, needsContract, contract, canProceed, retryContract, acceptContract } = useCheckout()
  const list = useGroupedBasket()

  const { push, replace } = useHistory()
  const { state: locationState } = useLocation()
  const getName = useWarehouseName()
  const dispatch = useReduxDispatch()
  const { t } = useTranslation('basket')
  const { finalizeBasket } = useBasketFinalize(setFinalized)
  useOrderRedirect()

  useEffect(() => {
    if (!isLoading && isCustomerDataIncomplete(customerValidation)) {
      replace('/contract')
    }
  }, [customerValidation, isLoading, replace])

  // Niezweryfikowany telefon: zamiast checkboxa umowa podpisywana kodem SMS z poziomu podsumowania
  const isSignatureMode = needsContract && mode === CONTRACT_MODES.signature
  const isSigned = isSignatureMode && isContractGroupSigned(contract.group)

  // Ważny podpis = akceptacja umowy, więc dalsze kroki wysyłają contract_accepted: true
  useEffect(() => {
    if (isSigned && !terms.rentalTerms) {
      dispatch(updateTerms({ ...terms, rentalTerms: true }))
    }
  }, [dispatch, isSigned, terms])

  // Po podpisie SignAgreementPopup sam robi goBack() - na płatność przechodzimy dopiero po zamknięciu popupu
  useEffect(() => {
    if (isSignedPending && !locationState?.popup) {
      setSignedPending(false)
      window.scrollTo({ top: 0 })
      push('/order/payment')
    }
  }, [isSignedPending, locationState, push])

  const privacyPolicyConsent = t('privacyPolicyConsent')
  const termsAndConditionsAcceptance = t('termsAndConditionsAcceptance')
  const generalRentalTermsAcceptance = t('generalRentalTermsAcceptance')
  const isWarehouseInOrder = list?.some(l => l.type === BASKET_TYPES.rent)
  const showRentalTerms = !config?.contractSigningScreenEnabled && isWarehouseInOrder && !isSignatureMode
  const contractSigningConsent = t('contractSigningConsent')

  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  const Link = ({ type, children }) => <a
    className='--link'
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      openPopup({ type: 'consent', content: type })
    }}>
    {children}
  </a>

  // Podgląd umowy w popupie bez wychodzenia z koszyka (czas rezerwacji biegnie dalej)
  // eslint-disable-next-line jsx-a11y/anchor-is-valid
  const ContractLink = ({ pdf, isTemplate, children }) => <a
    className='--link'
    onClick={e => {
      e.preventDefault()
      e.stopPropagation()
      openPopup({ type: 'contractPreview', props: { contractPdf: pdf, isTemplate } })
    }}>
    {children}
  </a>

  const getContractButton = text => {
    if (contract.pdf) return <ContractLink pdf={contract.pdf}>{text}</ContractLink>
    if (!needsContract && contractTemplateUrl) return <ContractLink pdf={contractTemplateUrl} isTemplate>{text}</ContractLink>
    return text
  }

  const contractStatus = needsContract && <>
    {contract.isLoading && <div className='summary__contract'>
      {t('contractLoading')}
    </div>}
    {contract.isError && <div className='summary__contract --error'>
      <span>{t('contractGenerationFailed')}</span>
      <span
        className='--link'
        onClick={retryContract}>
        {t('tryAgain')}
      </span>
      {contractTemplateUrl && <ContractLink
        isTemplate
        pdf={contractTemplateUrl}>
        {t('showAgreementTemplate')}
      </ContractLink>}
    </div>}
  </>

  const buttonText = (() => {
    if (config?.contractSigningScreenEnabled) {
      return (contractConfirmation === 'print' || contractConfirmation === 'phone' || isContractGroupSigned(contractGroups?.[0]))
        ? 'next'
        : 'signTheContract'
    }
    return isSignatureMode && !isSigned ? 'signTheContract' : 'pay'
  })()

  return (
    <>
      {popup?.type === 'consent' && <ConsentPreviewPopup
        type={popup.content}
      />}

      {popup?.type === 'signAgreement' && contract.groupId && <SignAgreementPopup
        contractGroupId={contract.groupId}
        callback={(signature, replacedBy) => {
          dispatch(contractGroupSigned({ id: contract.groupId, replacedBy, signature }))
          setSignedPending(true)
        }}
      />}

      <div className='summary'>
        <div className='title'>
          <div>{t('summary')}</div>
          <div>{t('checkData')}</div>
        </div>

        <div className='summary__content'>
          <div className='summary__content__left'>
            {tenant?.user
              ? tenant.company
                ? <div>
                  <div className='list__header element__title'>{t('invoiceDetails')}</div>
                  <div className='list__element --big'>
                    <div className='element__title'>
                      <div>{tenant?.company?.name}</div>
                      <div>NIP: {tenant?.company?.nip}</div>
                    </div>
                    <div className='element__desc'>
                      {tenant?.company?.address && <div>{tenant?.company?.address}</div>}
                      <div>{t('representative')}: {tenant.user.name}</div>
                    </div>
                  </div>
                </div>
                : <div>
                  <div className='list__header element__title'>{t('contractData')}</div>
                  <div className='list__element --big'>
                    <div className='element__title'>
                      <div>{tenant.user.name}</div>
                      {tenant.user.pesel && <div>PESEL: {tenant.user.pesel}</div>}
                    </div>
                    <div className='element__desc'>
                      {tenant.user.address && <div>{tenant.user.address}</div>}
                      {(tenant.user.city || tenant.user.zip) && <div>{tenant.user.zip} {tenant.user.city}</div>}
                      {tenant.user.phone && <div>tel. {toPhoneString(tenant.user.phone, tenant.user.prefix)}</div>}
                      {tenant.user.email && <div>email: {tenant.user.email}</div>}
                    </div>
                  </div>
                </div>
              : <UserSection />}

            {list?.length > 0 && list.map((item) => item.type === BASKET_TYPES.rent
              ? <MagSection
                {...item}
                name={getName(item)}
                key={item.identifier}
              />
              : <AddonSection
                {...item}
                key={item.identifier}
              />)}

            <DeliverySection />
          </div>

          <div className='summary__content__right'>
            <SummarySection list={list} />

            {(invoice || tenant?.company) && (invoice || tenant?.company)?.companyFormName !== 'jednoosobowa działalność gospodarcza' && <div>
              <div className='list__header element__title'>{t('forBusinessCustomers')}</div>
              <Option
                icon='contract'
                title={t('needDeferredPaymentTerms')}
                buttonText={t('callUs')}
                action={config?.phoneSupport
                  ? () => {
                    window.location.href = `tel:${config.phoneSupport}`
                  } : undefined}
                additionalContent={<div className='summary__additional'>
                  <div className='summary__additional__desc'>
                    {t('deferredInvoiceVerificationDescription')}
                  </div>
                  {contractTemplateUrl && <>
                    <hr />
                    <div className='summary__additional__title'>
                      {t('wantToReviewAgreementBeforeSigning')}
                    </div>
                    <div
                      className='summary__additional__link'
                      onClick={() => {
                        window.open(contractTemplateUrl)
                      }}>
                      <i className='icon icon--download' />
                      <span>{t('agreementTemplate')}</span>
                    </div>
                  </>}
                </div>}
              />
            </div>}

            <div>
              <div className='list__header element__title'>{t('statements')}</div>
              <div className='list__element --big'>
                <div className='element__title'>
                  <span>{t('IDeclareThat')}:</span>
                </div>

                <div className='element__desc'>
                  <Checkbox
                    name='termsAndConditions'
                    checked={terms.termsAndConditions}
                    warning={error.termsAndConditions}
                    label={<>{termsAndConditionsAcceptance?.introduction} <Link type='terms'>{termsAndConditionsAcceptance?.button}</Link></>}
                    changeHandler={() => {
                      if (!terms?.termsAndConditions && error.termsAndConditions) {
                        setError(prev => ({ ...prev, termsAndConditions: false }))
                      }
                      dispatch(updateTerms({ ...terms, termsAndConditions: !terms.termsAndConditions }))
                    }}
                  />
                  <Checkbox
                    name='privacyPolicy'
                    checked={terms.privacyPolicy}
                    warning={error.privacyPolicy}
                    label={<>{privacyPolicyConsent?.introduction} <Link type='privacy'>{privacyPolicyConsent?.button}</Link></>}
                    changeHandler={() => {
                      if (!terms?.privacyPolicy && error.privacyPolicy) {
                        setError(prev => ({ ...prev, privacyPolicy: false }))
                      }
                      dispatch(updateTerms({ ...terms, privacyPolicy: !terms.privacyPolicy }))
                    }}
                  />
                  {showRentalTerms && <Checkbox
                    name='rentalTerms'
                    checked={terms.rentalTerms}
                    warning={error.rentalTerms}
                    disabled={needsContract && !contract.pdf}
                    label={<>{generalRentalTermsAcceptance?.introduction} {getContractButton(generalRentalTermsAcceptance?.button)} {generalRentalTermsAcceptance?.conclusion}</>}
                    changeHandler={() => {
                      if (!terms?.rentalTerms && error.rentalTerms) {
                        setError(prev => ({ ...prev, rentalTerms: false }))
                      }
                      dispatch(updateTerms({ ...terms, rentalTerms: !terms.rentalTerms }))
                    }}
                  />}
                  {showRentalTerms && contractStatus}
                </div>
              </div>
            </div>

            {isSignatureMode && <div>
              <div className='list__header element__title'>{t('contractSection')}</div>
              <div className='list__element --big'>
                <div className='element__desc'>
                  <div>{contractSigningConsent?.introduction} {getContractButton(contractSigningConsent?.button)} {contractSigningConsent?.conclusion}</div>
                  {isSigned && <div className='summary__contract'>{t('contractSignedInfo')}</div>}
                  {contractStatus}
                </div>
              </div>
            </div>}


            <div className='summary__buttons'>
              <ArrowBtn
                enlarged
                text={t(buttonText)}
                isLoading={isLoading || isFinalized || isSubmitting}
                action={async () => {
                  if (isLoading || isSubmitting) return
                  if (canProceed && terms.privacyPolicy && terms.termsAndConditions && (showRentalTerms ? terms.rentalTerms : true)) {
                    if (isSignatureMode) {
                      if (isSigned) {
                        window.scrollTo({ top: 0 })
                        push('/order/payment')
                      } else {
                        // Modal podpisu od razu w kroku wysyłki kodu SMS (z możliwością zmiany numeru / przelogowania)
                        openPopup({ type: 'signAgreement' })
                      }
                      return
                    }
                    if (needsContract) {
                      // Zgoda (contract_accepted) idzie razem ze stepem "contract" - bez potwierdzenia nie przechodzimy dalej
                      setSubmitting(true)
                      const isAccepted = await acceptContract()
                      setSubmitting(false)
                      if (!isAccepted) return
                    }
                    window.scrollTo({ top: 0 })
                    if (isPaid && (!config?.contractSigningScreenEnabled || (contractGroups?.[0] && !contractGroups?.[0]?.pdf))) {
                      finalizeBasket(() => {
                        dispatch(finalizeBasketManually())
                      })
                    } else {
                      push(config?.contractSigningScreenEnabled
                        ? '/order/contract'
                        : '/order/payment')
                    }
                  } else {
                    setError({
                      privacyPolicy: !terms.privacyPolicy,
                      termsAndConditions: !terms.termsAndConditions,
                      rentalTerms: showRentalTerms ? !terms.rentalTerms : false
                    })
                  }
                }} />
              {!tenant && <BackBtn />}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default OrderSummary